import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

type Props = {
  label: string;
  /** Where the tooltip sits relative to the trigger. */
  side?: 'right' | 'bottom';
  /** For `bottom`: `end` right-aligns the tooltip to the trigger (use near the screen edge). */
  align?: 'center' | 'end';
  /** Turn the tooltip off without unmounting the trigger. */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

type Pos = { top: number; left: number };

/**
 * Dark rounded tooltip rendered in a portal so scroll containers never clip it.
 * Wrap the trigger; the wrapper listens for hover/focus.
 */
export function Tooltip({ label, side = 'right', align = 'center', disabled, className, children }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  const show = () => {
    if (disabled || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    if (side === 'right') {
      setPos({ top: r.top + r.height / 2, left: r.right + 16 });
    } else {
      setPos({ top: r.bottom + 12, left: align === 'end' ? r.right : r.left + r.width / 2 });
    }
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={ref}
      className={cn('inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={hide}
    >
      {children}
      {pos && !disabled
        ? createPortal(
            <span
              role="tooltip"
              style={{ top: pos.top, left: pos.left }}
              className={cn(
                'pointer-events-none fixed z-[100] whitespace-nowrap rounded-xl bg-[#0f172b] px-4 py-2 text-sm font-semibold text-white shadow-lg dark:bg-[#1e293b]',
                side === 'right' ? '-translate-y-1/2' : align === 'end' ? '-translate-x-full' : '-translate-x-1/2'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'absolute h-2.5 w-2.5 rotate-45 rounded-[2px] bg-[#0f172b] dark:bg-[#1e293b]',
                  side === 'right'
                    ? '-left-1 top-1/2 -translate-y-1/2'
                    : cn('-top-1', align === 'end' ? 'right-4' : 'left-1/2 -translate-x-1/2')
                )}
              />
              {label}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}
